import React from 'react';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Grid from '@mui/material/Grid';

import InputWithLabel from '@ui-parts/InputWithLabel';
import { FONT_SIZES } from '~/assets/styles/base';

const Wrapper = styled.div``;

const InputWrapper = styled.div`
  width: ${({ $width }) => $width || 'max-content'};
  padding: 5px 0;
  // コンポーネント側で調整できるようにラッパーにてデフォルトフォントサイズを設定
  .MuiOutlinedInput-input.MuiInputBase-input {
    font-size: ${({ $shouldInheritFontSize }) =>
      $shouldInheritFontSize || FONT_SIZES.modalInputDefault};
  }
`;

const justifyContents = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

function ModalBodyForInput({ control, inputs }) {
  return (
    <Wrapper>
      {inputs.map((input, i) => {
        // errorZIndexが存在しなければ10000を渡す
        const errorZIndex = input.errorZIndex ? input.errorZIndex : 10000;
        if (input.position) {
          return (
            <Grid
              key={i}
              container
              justifyContent={justifyContents[input.position]}
              data-testid={`uiParts-ModalBodyForInput-Grid-${i}`}
            >
              <InputWrapper
                $width={input.width}
                $shouldInheritFontSize={input.inputFontSize ? 'inherit' : false}
                data-testid={`uiParts-ModalBodyForInput-Wrapper-${i}`}
              >
                <Controller
                  name={input.nameProp}
                  control={control}
                  rules={input.rules}
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error },
                  }) => {
                    return (
                      <InputWithLabel
                        label={input.label}
                        name={name}
                        type={input.type}
                        value={value}
                        required={input.required}
                        disabled={input.disabled}
                        placeholder={input.placeholder}
                        helperText={input.helperText}
                        errorMessage={error?.message}
                        width={input.width}
                        textAlign={input.textAlign}
                        inputFontSize={input.inputFontSize}
                        handleChange={(e) => {
                          if (input.onlyNum) {
                            // 最初の0を取るためにNumber()して、バリデーションを発火させるために最後にString()する
                            onChange(String(Number(e.target.value.replace(/[^0-9]/g, ''))));
                          } else {
                            onChange(e);
                          }

                          if (input.handleChange) {
                            input.handleChange(e);
                          }
                        }}
                        handleBlur={(e) => {
                          onBlur(e);
                          if (input.handleBlur) {
                            input.handleBlur(e);
                          }
                        }}
                        error={Boolean(error)}
                        errorWidth={input.errorWidth}
                        errorZIndex={errorZIndex}
                        dataTestId={input.dataTestId}
                        inputProps={input.inputProps}
                        multiline={input.multiline}
                        rows={input.rows}
                        ref={ref}
                      />
                    );
                  }}
                />
              </InputWrapper>
            </Grid>
          );
        }
        return (
          <InputWrapper key={i} data-testid={`uiParts-ModalBodyForInput-Wrapper-${i}`}>
            <Controller
              name={input.nameProp}
              control={control}
              rules={input.rules}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { error },
              }) => {
                return (
                  <InputWithLabel
                    label={input.label}
                    name={name}
                    type={input.type}
                    value={
                      input.onlyNum && value !== undefined
                        ? Number(String(value).replace(/[^0-9]+/i, ''))
                        : value
                    }
                    required={input.required}
                    disabled={input.disabled}
                    placeholder={input.placeholder}
                    helperText={input.helperText}
                    errorMessage={error?.message}
                    width={input.width}
                    textAlign={input.textAlign}
                    handleChange={(e) => {
                      if (input.handleChange) {
                        input.handleChange(e);
                      }
                      onChange(e);
                    }}
                    handleBlur={(e) => {
                      if (input.handleBlur) {
                        input.handleBlur(e);
                      }
                      onBlur(e);
                    }}
                    error={Boolean(error)}
                    errorZIndex={errorZIndex}
                    dataTestId={input.dataTestId}
                    inputProps={input.inputProps}
                    multiline={input.multiline}
                    rows={input.rows}
                    ref={ref}
                  />
                );
              }}
            />
          </InputWrapper>
        );
      })}
    </Wrapper>
  );
}

ModalBodyForInput.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  control: PropTypes.object,
  inputs: PropTypes.arrayOf(
    PropTypes.shape({
      // ↓rulesは動的なのでいったんlintエラーは無視してobjectで定義
      // eslint-disable-next-line react/forbid-prop-types
      rules: PropTypes.object,
      label: PropTypes.string,
      nameProp: PropTypes.string.isRequired,
      type: PropTypes.string,
      required: PropTypes.bool,
      disabled: PropTypes.bool,
      placeholder: PropTypes.string,
      helperText: PropTypes.string,
      width: PropTypes.string,
      handleChange: PropTypes.func,
      handleBlur: PropTypes.func,
      onSubmit: PropTypes.func,
      position: PropTypes.oneOf(['center', 'left', 'right']),
      textAlign: PropTypes.oneOf(['center', 'left', 'right']),
      errorWidth: PropTypes.string,
      errorZIndex: PropTypes.number,
      dataTestId: PropTypes.string,
      onlyNum: PropTypes.bool,
      // eslint-disable-next-line react/forbid-prop-types
      inputProps: PropTypes.object,
      multiline: PropTypes.bool,
      rows: PropTypes.number,
    })
  ).isRequired,
};

export default ModalBodyForInput;
